import React, { useState } from 'react';
import styles from './ApiDetails.module.scss';
import JsonViewer from '../JsonViewer';
import { Tabs, Tab } from '../Tabs';
import Recipes from '../Recipes';

const ApiDetails = ({ api, paramValues, handleParamChange, workflowMap, onWorkflowClick }) => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (paramName, index) => {
    const key = `${paramName}-${index}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!api) {
    return (
      <div className={styles.apiDetailsContainer}>
        <div className={styles.welcomeMessage}>
          <h2>Welcome to the API Reference</h2>
          <p>Select an API from the sidebar to view its documentation.</p>
        </div>
      </div>
    );
  }

  const handleInputChange = (name, value, schemaType) => {
    if (schemaType === 'number' || schemaType === 'integer') {
      // Allow only digits by removing all non-digit characters
      const sanitizedValue = value.replace(/[^0-9]/g, '');
      handleParamChange(name, sanitizedValue);
    } else {
      handleParamChange(name, value);
    }
  };

  return (
    <div className={styles.apiDetailsContainer}>
      <div className={styles.header}>
        <h2>{api.name}</h2>
        <div className={styles.apiMethodPath}>
          <span className={`${styles.method} ${styles[api.method.toLowerCase()]}`}>{api.method}</span>
          <span className={styles.path}>{api.path}</span>
        </div>
      </div>
      
      <div className={styles.overviewSection}>
        <div dangerouslySetInnerHTML={{ __html: api.description.split("<")[0] }} />
        {api.embeddedSamples && api.embeddedSamples.map((sample, index) => (
          <JsonViewer key={index} title={`Example ${index + 1}`} json={sample} />
        ))}
      </div>

      <Recipes
        api={api}
        workflowMap={workflowMap}
        onWorkflowClick={onWorkflowClick}
      />

      <Tabs>
        <Tab label="Parameters">
          <div className={styles.parametersSection}>
            {
              (() => {
                const allParameters = api.parameters || [];
                const groupedParams = allParameters.reduce((acc, param) => {
                  const group = param.in.charAt(0).toUpperCase() + param.in.slice(1);
                  if (!acc[group]) {
                    acc[group] = [];
                  }
                  acc[group].push(param);
                  return acc;
                }, {});

                if (Object.keys(groupedParams).length === 0) return <p>No parameters for this endpoint.</p>;

                return Object.entries(groupedParams).map(([groupName, params]) => (
                  <div key={groupName} className={styles.parameterGroup}>
                    <h4>{groupName} Parameters</h4>
                    <div className={styles.parametersList}>
                      {params.map((param) => {
                        if (param.schema.type === 'array' && param.schema.items && param.schema.items.properties) {
                          const itemSchema = param.schema.items;
                          const arrayValues = paramValues[param.name] || [{}];
                      
                          return (
                            <div key={param.name} className={styles.arrayParameter}>
                              <div className={styles.parameterInfo}>
                                <div className={styles.parameterName}>
                                  {param.name}
                                  <span className={styles.parameterType}>{param.schema.type}</span>
                                  {param.required && <span className={styles.requiredStar}>* required</span>}
                                </div>
                                <div className={styles.parameterDescription} dangerouslySetInnerHTML={{ __html: param.description }}></div>
                              </div>
                              {arrayValues.map((item, index) => (
                                <div key={index} className={styles.arrayItem}>
                                  <div className={styles.arrayItemHeader} onClick={() => toggleItem(param.name, index)}>
                                    <h5>{param.name.charAt(0).toUpperCase() + param.name.slice(1)} Item {index + 1}</h5>
                                    <div className={styles.arrayItemControls}>
                                      {(!param.required || index > 0) && (
                                        <button onClick={(e) => { e.stopPropagation(); handleParamChange('REMOVE_ARRAY_ITEM', { paramName: param.name, index }); }} className={styles.deleteButton}>Delete</button>
                                      )}
                                      <span className={`${styles.accordionIcon} ${openItems[`${param.name}-${index}`] ? styles.open : ''}`}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                                      </span>
                                    </div>
                                  </div>
                                  {openItems[`${param.name}-${index}`] && (
                                    <div className={styles.arrayItemBody}>
                                      {Object.keys(itemSchema.properties).map((key) => (
                                        <div key={key} className={styles.parameterRow}>
                                          <div className={styles.parameterInfo}>
                                            <div className={styles.parameterName}>
                                              {key}
                                              <span className={styles.parameterType}>{itemSchema.properties[key].type}</span>
                                              {itemSchema.required && itemSchema.required.includes(key) && <span className={styles.requiredStar}>* required</span>}
                                            </div>
                                            <div className={styles.parameterDescription} dangerouslySetInnerHTML={{ __html: itemSchema.properties[key].description }}></div>
                                          </div>
                                          <div className={styles.parameterInput}>
                                            <input
                                              type="text"
                                              inputMode={itemSchema.properties[key].type === 'integer' || itemSchema.properties[key].type === 'number' ? 'numeric' : 'text'}
                                              pattern={(itemSchema.properties[key].type === 'integer' || itemSchema.properties[key].type === 'number') ? '\\d*' : undefined}
                                              className={styles.paramInputField}
                                              placeholder={itemSchema.properties[key].type}
                                              value={item[key] || ''}
                                              onChange={(e) => {
                                                const schemaType = itemSchema.properties[key].type;
                                                const value = e.target.value;
                                                if (schemaType === 'number' || schemaType === 'integer') {
                                                  const sanitizedValue = value.replace(/[^0-9]/g, '');
                                                  handleParamChange('UPDATE_ARRAY_ITEM', { paramName: param.name, index, field: key, value: sanitizedValue });
                                                } else {
                                                  handleParamChange('UPDATE_ARRAY_ITEM', { paramName: param.name, index, field: key, value });
                                                }
                                              }}
                                            />
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                              <button onClick={() => handleParamChange('ADD_ARRAY_ITEM', { paramName: param.name })} className={styles.addButton}>Add Item</button>
                            </div>
                          );
                        }
                        
                        return (
                          <div key={param.name} className={styles.parameterRow}>
                            <div className={styles.parameterInfo}>
                              <div className={styles.parameterName}>
                                {param.name}
                                <span className={styles.parameterType}>{param.schema.type}</span>
                                {param.required && <span className={styles.requiredStar}>* required</span>}
                                {param.schema.default && <span className={styles.parameterDefault}>Defaults to {param.schema.default}</span>}
                              </div>
                              <div className={styles.parameterDescription} dangerouslySetInnerHTML={{ __html: param.description }}></div>
                            </div>
                            <div className={styles.parameterInput}>
                              <input
                                type="text"
                                inputMode={param.schema.type === 'integer' || param.schema.type === 'number' ? 'numeric' : 'text'}
                                pattern={(param.schema.type === 'integer' || param.schema.type === 'number') ? '\\d*' : undefined}
                                className={styles.paramInputField}
                                placeholder={param.in === 'header' ? '***' : param.schema.type}
                                value={paramValues[param.name] || ''}
                                onChange={(e) => handleInputChange(param.name, e.target.value, param.schema.type)}
                              />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ));
              })()
            }
          </div>
        </Tab>
        <Tab label="Responses">
          <div className={styles.responsesSection}>
            <h4>Responses</h4>
            <table className={styles.responsesTable}>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {api.responses && Object.entries(api.responses).map(([key, response]) => (
                  <React.Fragment key={key}>
                    <tr>
                      <td>
                        <div className={`${styles.statusCode} ${response.statusCode >= 400 ? styles.error : styles.success}`}>
                          {response.statusCode}
                        </div>
                      </td>
                      <td>{response.description}</td>
                    </tr>
                    {response.body && Object.keys(response.body).length > 0 && (
                      <tr className={styles.responseBodyRow}>
                        <td colSpan="2">
                          <JsonViewer json={response.body} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Tab>
      </Tabs>
    </div>
  );
};

export default ApiDetails;